'use client';
import { GetInfoItemsResult, InfoBarModule, InfoBarPriority } from './InfoBarModule';
import React, { ReactNode } from 'react';
import { NewsdeskNotification, NewsdeskNotificationType, useNewsdeskNotifications } from './useNewsdeskNotifications';
import styled, { TC } from '@benzinga/themetron';
import { openWindow } from '@benzinga/frontend-utils';
import { Close } from '@benzinga/themed-icons';

const openNotificationLink = (url: string) => {
  if (url) {
    const link = url.startsWith('http') ? url : `http://${url}`;
    openWindow(link, '_blank', 'width=750,height=700,left=0,resizable', undefined, {
      errorMessage: 'could not do it',
      free: true,
    });
  }
};
export class NewsdeskNotificationsModule extends InfoBarModule {
  public id = 'newsdesk-notifications';
  public name = 'Newsdesk Notifications';
  public priority = InfoBarPriority.Notification;
  public getInfoItems: Generator<GetInfoItemsResult, GetInfoItemsResult, number>;
  private onClick?: (id: string) => void;
  private notification: NewsdeskNotification | undefined = undefined;
  private notificationsEnabled = true;

  constructor() {
    super();
    this.getInfoItems = this.notificationGenerator();
  }

  *notificationGenerator(): Generator<GetInfoItemsResult, GetInfoItemsResult, number> {
    while (true) {
      const { notification, onClick } = this;

      yield {
        items:
          this.notificationsEnabled && notification
            ? [
                {
                  node: (
                    <NewsdeskNotificationWrapper $notificationType={notification.notificationType}>
                      {boldBeginningUppercaseWords(notification.title, notification.url)}
                      {onClick ? (
                        <CloseButton
                          onClick={() => onClick(notification._id ?? '')}
                          size="1.25em"
                          title="Close Notification"
                        />
                      ) : null}
                    </NewsdeskNotificationWrapper>
                  ),
                  width: 0,
                },
              ]
            : [],
      };
    }
  }

  public updateNotification(newNotification?: NewsdeskNotification, ignoreNotification?: (id: string) => void) {
    this.notification = newNotification;
    this.onClick = ignoreNotification;
    this.dispatch({ id: this.id, type: 'force_content_change' });
  }

  public setNotificationEnabled(notificationsEnabled: boolean) {
    this.notificationsEnabled = notificationsEnabled;
    this.dispatch({ id: this.id, type: 'force_content_change' });
  }
}

function boldBeginningUppercaseWords(title: string, url: string): ReactNode {
  let matchPosition = 0;
  const regex = title.match(/^[A-Z0-9 :apm.]{4,25}:/);
  if (regex) {
    matchPosition = regex[0]?.length ?? 0;
  }
  const hasMatch = matchPosition > 0;

  const beginningCaps = hasMatch ? title.substr(0, matchPosition) : null;
  const restOfTitle = hasMatch ? title.substr(matchPosition, title.length) : title;
  return (
    <NotificationText onClick={() => openNotificationLink(url)}>
      {beginningCaps && <strong>{beginningCaps}</strong>}
      {restOfTitle}
    </NotificationText>
  );
}

export const useNewsdeskNotificationsModule = (notificationEnabled: boolean): NewsdeskNotificationsModule => {
  const { ignoreNotification, notification } = useNewsdeskNotifications();
  const module = React.useMemo<NewsdeskNotificationsModule>(() => {
    return new NewsdeskNotificationsModule();
  }, []);

  React.useEffect(() => {
    module.updateNotification(notification, ignoreNotification);
  }, [module, notification, ignoreNotification]);

  React.useEffect(() => {
    module.setNotificationEnabled(notificationEnabled);
  }, [module, notificationEnabled]);

  return module;
};

const NewsdeskNotificationWrapper = styled(TC.StretchRow)<{ $notificationType: NewsdeskNotificationType }>`
  height: 100%;
  width: 100%;
  padding: 5px 5px 5px 20px;
  cursor: ${props => (props.onClick ? 'pointer' : 'default')};
  background-color: ${props =>
    props.$notificationType === NewsdeskNotificationType.breaking
      ? props.theme.colors.statistic.negative
      : props.theme.colors.statistic.positive};
`;

const NotificationText = styled.span`
  align-self: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
  color: ${props => props.theme.colorPalette.black};
  cursor: pointer;
`;

const CloseButton = styled(Close)`
  align-self: center;
  background: none;
  border: none;
  cursor: pointer;
  margin-left: 5px;
  stroke: ${props => props.theme.colorPalette.black};
  stroke-width: 2px;
  font-size: 16px;
`;
